import { useState } from 'react';

import lubavLoader from '@backrs_ui/common-ui/src/assets/animations/dots-dotting.json';
import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { CareerPathScoreSection } from '@backrs_ui/common-ui/src/models/careerPath';
import { HandleAPICallResponseReturn } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';

import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import Lottie from 'lottie-react';
import Markdown from 'react-markdown';

const DIALOG_NUGGET_FIT_STRINGS = appStrings.modules.nugget.careerPath.suggestedFits;
const DIALOG_NUGGET_STRINGS = appStrings.modules.nugget.dialogNuggetActions;

interface FitExplainerProps {
  disabled?: boolean;
  fit: CareerPathScoreSection;
  fitExplainerOnClick?: () => Promise<HandleAPICallResponseReturn | undefined>;
  fitExplainerText?: string;
}

export const FitExplainer = ({
  fit,
  fitExplainerOnClick,
  fitExplainerText,
  disabled,
}: FitExplainerProps) => {
  const [showFitExplainer, setShowFitExplainer] = useState(false);
  const [isLoadingFitExplainer, setIsLoadingFitExplainer] = useState(false);

  const handleFitExplainerClick = async () => {
    if (!fitExplainerOnClick || disabled) return;

    setShowFitExplainer(true);

    if (!showFitExplainer) {
      setIsLoadingFitExplainer(true);
      try {
        await fitExplainerOnClick();
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingFitExplainer(false);
      }
    } else {
      setShowFitExplainer((prev) => !prev);
    }
  };

  const getFitString = (fit: CareerPathScoreSection) => {
    switch (fit) {
      case 'TOP':
        return DIALOG_NUGGET_FIT_STRINGS.strong;
      case 'MIDDLE':
        return DIALOG_NUGGET_FIT_STRINGS.medium;
      default:
        return DIALOG_NUGGET_FIT_STRINGS.low;
    }
  };

  const ButtonComponent = (
    <Box sx={getFitExplainerButtonStyle(disabled)} onClick={handleFitExplainerClick}>
      {DIALOG_NUGGET_STRINGS.explainFitScore}
      {isLoadingFitExplainer && (
        <Box sx={loaderBoxStyle}>
          <Lottie animationData={lubavLoader} loop style={animationStyle} />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={fitContainerStyle}>
        <Box sx={getFitContainerStyle(fit)}>{getFitString(fit)}</Box>
        {disabled ? (
          <Tooltip
            title={appStrings.modules.nugget.askLubav.errors.NOT_LOGGED_IN}
            arrow
            componentsProps={{
              popper: {
                sx: {
                  [`&.MuiTooltip-popper`]: {
                    zIndex: 99999,
                  },
                },
              },
            }}
          >
            {ButtonComponent}
          </Tooltip>
        ) : (
          ButtonComponent
        )}
      </Box>
      {showFitExplainer && fitExplainerText && (
        <Box sx={fitExplanationText}>
          <Markdown>{fitExplainerText}</Markdown>
        </Box>
      )}
    </>
  );
};

// Styles (copied from original file)
const fitContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  marginTop: 1,
};

const fitExplainerButton: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  fontWeight: 700,
  color: appColors.color.blue1,
  cursor: 'pointer',
  backgroundColor: appColors.color.blue10,
  borderRadius: '8px',
  padding: '4px 8px',
  width: 'fit-content',
};

const fitExplanationText: SxProps<Theme> = {
  backgroundColor: appColors.color.blue10,
  paddingY: 2,
  paddingX: 3,
  borderRadius: 2,
  marginTop: 1,
  width: '100%',
};

const loaderBoxStyle: SxProps<Theme> = {
  height: '16px',
  width: '16px',
  marginLeft: 1,
} as const;

const animationStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
} as const;

const baseFitContainerStyle: SxProps<Theme> = {
  fontSize: '14px',
  borderRadius: '8px',
  padding: '4px 8px',
  width: 'fit-content',
  fontWeight: 700,
};

const getFitExplainerButtonStyle = (disabled?: boolean): SxProps<Theme> => ({
  ...fitExplainerButton,
  cursor: disabled ? 'default' : 'pointer',
  backgroundColor: disabled ? `${appColors.neutral.black}16` : appColors.color.blue10,
  color: disabled ? `${appColors.neutral.black}70` : appColors.color.blue1,
});

const getFitContainerStyle = (scoreSection: CareerPathScoreSection): SxProps<Theme> => {
  switch (scoreSection) {
    case 'TOP':
      return {
        ...baseFitContainerStyle,
        backgroundColor: appColors.color.navy2,
        color: appColors.neutral.white,
      };
    case 'MIDDLE':
      return {
        ...baseFitContainerStyle,
        backgroundColor: appColors.color.blue1,
        color: appColors.neutral.white,
      };
    default:
      return {
        ...baseFitContainerStyle,
        backgroundColor: `${appColors.color.blue1}36`,
        color: appColors.neutral.black,
      }; // LOW
  }
};
